import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import createQueryParam from "@/helpers/createQueryParam";
import { applicationReviewByAliasKey } from "@/hooks/apis/queries/useReviewByIdQuery/constants";
import {
  ReviewByIdQueryParamsKeyEnum,
  ReviewByIdQueryParamsValueEnum,
} from "@/hooks/apis/queries/useReviewByIdQuery/types";
import useAppPathname from "@/hooks/common/useAppPathname/useAppPathname";
import { useAppRouter } from "@/hooks/common/useAppRouter";
import { useAuthUser } from "@/hooks/common/useAuthUser";

import type { PureNullable } from "@/common/globalTypes/utillityTypes/common";
import { applicationsApiHandler } from "@/services/apiHandlers/applications";

const useReviewByIdQuery = () => {
  const [isShowEditOrAddModalReview, setIsShowEditOrAddModalReview] =
    useState(false);

  const { logIn, user, isLogged: isLoggedChecking } = useAuthUser();
  const router = useAppRouter();
  const pathname = useAppPathname();
  const searchParams = useSearchParams();

  const reviewAction = searchParams.get(
    ReviewByIdQueryParamsKeyEnum.reviewAction,
  );

  const reviewId = searchParams.get(ReviewByIdQueryParamsKeyEnum.reviewId);

  const isEditAction = reviewAction === ReviewByIdQueryParamsValueEnum.edit;
  const enabled = Boolean(
    isShowEditOrAddModalReview && isEditAction && reviewId,
  );

  const { data: appReview, isLoading: isLoadingAppReview } = useQuery({
    queryKey: [applicationReviewByAliasKey, reviewId],
    queryFn: () => applicationsApiHandler.getApplicationReviewById(reviewId),
    gcTime: 0,
    enabled,
  });

  const onCloseReviewModal = () => {
    const queryParams = createQueryParam(
      {
        [ReviewByIdQueryParamsKeyEnum.reviewAction]:
          ReviewByIdQueryParamsValueEnum.add,
        [ReviewByIdQueryParamsKeyEnum.reviewId]: reviewId,
      },
      "delete",
    );

    router.appReplace(queryParams, { scroll: false });
  };

  useEffect(() => {
    const isCloseFeedbackForAppModal =
      isLoggedChecking && enabled && appReview && appReview.userId !== user?.id;

    if (isCloseFeedbackForAppModal) {
      onCloseReviewModal();
    }
  }, [enabled, appReview, user?.id]);

  const checkUserAuth = useCallback(
    async (reviewaction: PureNullable<string>) => {
      if (reviewaction) {
        const isLogged = await logIn(`${pathname}?${searchParams.toString()}`);
        setIsShowEditOrAddModalReview(isLogged !== null);
      } else {
        setIsShowEditOrAddModalReview(false);
      }
    },
    [logIn, pathname, searchParams],
  );

  useEffect(() => {
    checkUserAuth(reviewAction);
  }, [checkUserAuth, reviewAction]);

  const handleReviewAction = useCallback(
    async (action: ReviewByIdQueryParamsValueEnum, editedReviewId?: number) => {
      const isLogged = await logIn();

      if (isLogged !== null) {
        const queryParams = createQueryParam(
          {
            ...(editedReviewId && {
              [ReviewByIdQueryParamsKeyEnum.reviewId]: editedReviewId,
            }),
            [ReviewByIdQueryParamsKeyEnum.reviewAction]: action,
          },
          "create",
        );

        router.appReplace(queryParams, { scroll: false });
      }
    },
    [logIn, router],
  );

  const onEditReviewHandler = (idReview: number) =>
    handleReviewAction(ReviewByIdQueryParamsValueEnum.edit, idReview);

  const onAddReviewHandler = () =>
    handleReviewAction(ReviewByIdQueryParamsValueEnum.add);

  return {
    appReview,
    isLoadingAppReview: isEditAction && isLoadingAppReview,
    onEditReviewHandler,
    onAddReviewHandler,
    onCloseReviewModal,
    isShowEditOrAddModalReview,
  };
};

export default useReviewByIdQuery;
